import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { CircularProgress } from "@mui/material";
import { SovosBox, SovosButton, SovosSnackbar, SovosTooltip } from "mosaic-react";
import { CheckCircle } from "mosaic-react-icons";
import { SovosAIIcon } from "mosaic-react-platform";
import {
    AI_BRAND_GRADIENT_ICON_LEAD,
    aiBrandLinearGradient315,
} from "mosaic-react-platform/shared/aiBrandGradient";
import {
    useControllerInitialization,
    organizationAndProjectSelection,
    buildGenericMenu,
    GlobalPageLayout,
    buildNavProps,
    generateNavigation,
    getDownloadableQueries,
    buildHeaderComponents,
} from "../utils/utils";
import { applyThemeWithPriority, buildStaticLinks, hasModule } from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import useUpdateThemes from "../../hooks/useUpdateThemes";
import proxy from "../../routes/proxy";
import { matchRoutes } from "react-router-config";
import { asideRepositoryNavigationByModule } from "../Repository/navigation";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    toggleMenu,
    setLocale,
    removeModal,
    getImportProfileByIdAction,
    runCustomTemplateMappingAction,
    getAvailableMappingContextsAction,
    getAvailableEntitiesAction,
    getAvailableCdmFieldsAction,
    createImportProfileMappingAction,
    getImportProfileMappingByProfileAction,
    deleteImportProfileAction,
    suggestCustomTemplateEntityAction,
    suggestCustomTemplateMappingAction,
    suggestCustomTemplateFiscalYearAction,
    updateImportProfileMappingAction,
    getImportProfileIssuesAction,
    fixImportProfileCellAction,
    listStagingTranslationsAction,
    createStagingTranslationAction,
    updateStagingTranslationAction,
    toggleStagingTranslationStateAction,
    softDeleteStagingTranslationAction,
    applyStagingTranslationAction,
    suggestDataFixesAction,
    acceptDataFixesAction,
} from "../../store/actions";
import ColumnMappingCanvas from "../../store/views/MappingFlow/ColumnMappingCanvas";
import StagingDataPanel from "../../store/views/MappingFlow/StagingDataPanel/StagingDataPanel";
import {
    getParentTabFromSearch,
    PARENT_TAB_STAGING,
    setParentTabInSearch,
} from "../../store/views/MappingFlow/columnMappingFlowTabs";
import { parseColumnMappings } from "../../store/views/MappingFlow/mappedSourceColumns";
import ProceedFiscalYearDialog from "../../store/views/MappingFlow/ColumnMappingCanvas/components/ProceedFiscalYearDialog";
import ImportCompleteView from "../../store/views/MappingFlow/ImportCompleteView";
import {
    buildFiscalYearOptions,
    getSuggestedFiscalYear,
    resolveProceedImportId,
} from "../../store/views/MappingFlow/nextImportProfile";
import { trackUserAction } from "../../utils/pendo";
import {
    buildCustomTemplateEditPath,
    buildDataChangeLogPath,
    buildDataProcessingLogPath,
    buildEntityLogFilter,
    buildEntityNameLogFilter,
    buildImportMappingBasePath,
    resolveImportProfileLogEntity,
} from "../../utils/importProfileMenuActions";
import { filteredQueriesByModules } from "../../routes/manageRouting";
import Header from "./ColumnMappingFlowHeader";
import { FLOW_STEP_INDEX } from "../../store/views/MappingFlow/ColumnMappingCanvas/constants";
import SoviGradientOutlineButton from "../../store/views/MappingFlow/ColumnMappingCanvas/components/SoviGradientOutlineButton";
import { mapProperties } from "colbi_web_ui/lib/utils";
import { checkRolesByOrganizationProject } from "colbi_web_ui/lib/utils/roleValidator";
import { UserRoles } from "../../enums/userRoles";

const SOVI_BUTTON_GRADIENT = aiBrandLinearGradient315(AI_BRAND_GRADIENT_ICON_LEAD);
const SOVI_PLACEHOLDER_ORB_SX = {
    background: (theme) => aiBrandLinearGradient315(theme.palette.primary.main),
    borderRadius: (theme) => theme.shape.borderRadiusRound || "50%",
    flexShrink: 0,
    height: (theme) => theme.spacing(1.25),
    width: (theme) => theme.spacing(1.25),
};

const SoviAiIcon = () => (
    <SovosBox sx={{ alignItems: "center", display: "flex", "& svg": { height: 16, width: 16 } }}>
        <SovosAIIcon />
    </SovosBox>
);

const ColumnMappingFlow = (props) => {
    const dispatch = useDispatch();
    const [canvasStep, setCanvasStep] = useState(null);
    const [canvasImported, setCanvasImported] = useState(false);
    const [mappingPersisted, setMappingPersisted] = useState(false);
    const [mappingLoading, setMappingLoading] = useState(true);
    const [canvasActions, setCanvasActions] = useState({});
    const [importProfileName, setImportProfileName] = useState("");
    const [stagingAiBusy, setStagingAiBusy] = useState(false);
    const stagingDataPanelRef = useRef(null);
    // Column names that had at least one non-empty value across every file
    // ever uploaded for this profile (from profile_headers metadata). Columns
    // missing from this set have never had data, so they default to "Do not
    // import" instead of forcing the user to skip them one by one.
    const [columnsWithData, setColumnsWithData] = useState(null);
    // Allows deep-linking straight into the Staging Data tab (e.g. from the
    // Import Profiles grid, when a profile already has a mapping) via ?tab=staging.
    const [activeTab, setActiveTab] = useState(() =>
        getParentTabFromSearch(props.location?.search),
    );
    const [savedMapping, setSavedMapping] = useState(null);
    const [columnMappings, setColumnMappings] = useState([]);
    const [importComplete, setImportComplete] = useState(false);
    const [showProceedDialog, setShowProceedDialog] = useState(false);
    const [proceeding, setProceeding] = useState(false);
    const [proceedError, setProceedError] = useState(null);
    const [menuSnackbar, setMenuSnackbar] = useState(null);
    const [aiFiscalYear, setAiFiscalYear] = useState("");
    const [aiFiscalYearScore, setAiFiscalYearScore] = useState(null);
    const [aiFiscalYearRationale, setAiFiscalYearRationale] = useState("");
    const [aiFiscalYearLoading, setAiFiscalYearLoading] = useState(false);
    const fiscalYearSuggestSeq = useRef(0);

    const handleStepChange = useCallback(
        ({ step, imported, hasPersistedMapping, mappingLoading: isMappingLoading }) => {
            setCanvasStep(step);
            setCanvasImported(imported);
            setMappingPersisted(Boolean(hasPersistedMapping));
            setMappingLoading(Boolean(isMappingLoading));
        },
        [],
    );

    const handleActionStateChange = useCallback((actions) => {
        setCanvasActions(actions);
    }, []);

    const handleMappingSaved = useCallback((info) => {
        setSavedMapping(info);
    }, []);

    const dispatchGetAvailableMappingContexts = useCallback(
        (currentProjectId) => dispatch(getAvailableMappingContextsAction(currentProjectId)),
        [dispatch],
    );
    const dispatchGetImportProfileById = useCallback(
        (currentProjectId, currentProfileId) =>
            dispatch(getImportProfileByIdAction(currentProjectId, currentProfileId)),
        [dispatch],
    );
    const dispatchRunCustomTemplateMapping = useCallback(
        (payload) => dispatch(runCustomTemplateMappingAction(payload)),
        [dispatch],
    );
    const dispatchGetAvailableEntities = useCallback(
        (configSet, legalStructure) =>
            dispatch(getAvailableEntitiesAction(configSet, legalStructure)),
        [dispatch],
    );
    const dispatchGetAvailableCdmFields = useCallback(
        (configSet, entityName, fileType) =>
            dispatch(getAvailableCdmFieldsAction(configSet, entityName, fileType)),
        [dispatch],
    );
    const dispatchCreateImportProfileMapping = useCallback(
        (payload) => dispatch(createImportProfileMappingAction(payload)),
        [dispatch],
    );
    const dispatchGetImportProfileMappingByProfile = useCallback(
        (profileIdArg) => dispatch(getImportProfileMappingByProfileAction(profileIdArg)),
        [dispatch],
    );

    const dispatchUpdateImportProfileMapping = useCallback(
        (mappingId, projectIdArg, data) =>
            dispatch(updateImportProfileMappingAction(mappingId, projectIdArg, data)),
        [dispatch],
    );
    const dispatchDeleteImportProfile = useCallback(
        (currentProfileId, currentProjectId) =>
            dispatch(deleteImportProfileAction(currentProfileId, currentProjectId)),
        [dispatch],
    );
    const dispatchListStagingTranslations = useCallback(
        (currentProfileId, currentProjectId) =>
            dispatch(listStagingTranslationsAction(currentProfileId, currentProjectId)),
        [dispatch],
    );
    const dispatchCreateStagingTranslation = useCallback(
        (payload) => dispatch(createStagingTranslationAction(payload)),
        [dispatch],
    );
    const dispatchUpdateStagingTranslation = useCallback(
        (translationId, payload) =>
            dispatch(updateStagingTranslationAction(translationId, payload)),
        [dispatch],
    );
    const dispatchToggleStagingTranslationState = useCallback(
        (translationId, state) =>
            dispatch(toggleStagingTranslationStateAction(translationId, state)),
        [dispatch],
    );
    const dispatchSoftDeleteStagingTranslation = useCallback(
        (translationId) => dispatch(softDeleteStagingTranslationAction(translationId)),
        [dispatch],
    );
    const dispatchApplyStagingTranslation = useCallback(
        (translationId, currentProfileId, currentProjectId) =>
            dispatch(
                applyStagingTranslationAction(translationId, currentProfileId, currentProjectId),
            ),
        [dispatch],
    );
    const {
        modules,
        route,
        location,
        matchParams,
        projectId,
        locale,
        fetchResults,
        fetchStatus,
        i18n,
        userInformation,
        history,
        pushModal,
        reload,
    } = useControllerInitialization(props);

    let { fiscalYear, importId, entityRawData } = matchParams;
    const profileId = parseInt(entityRawData, 10) || undefined;
    const hasAiSuggestions = hasModule(modules, "ai_suggestions");

    const persistParentTab = useCallback(
        (tab) => {
            setActiveTab(tab);
            if (!history?.replace) return;
            const currentSearch = (location?.search || "").replace(/^\?/, "");
            const nextSearch = setParentTabInSearch(currentSearch, tab);
            if (nextSearch === currentSearch) return;
            history.replace({ search: nextSearch });
        },
        [history, location?.search],
    );

    const handleParentTabChange = persistParentTab;

    const handleMappingConfirmed = useCallback(() => {
        persistParentTab(PARENT_TAB_STAGING);
    }, [persistParentTab]);

    useEffect(() => {
        setActiveTab(getParentTabFromSearch(location?.search));
    }, [location?.search]);

    useEffect(() => {
        let isActive = true;

        if (!projectId || !profileId) {
            setImportProfileName("");
            setColumnsWithData(null);
            return () => {
                isActive = false;
            };
        }

        setImportProfileName("");
        setColumnsWithData(null);
        dispatchGetImportProfileById(projectId, profileId).then((profile) => {
            if (!isActive) return;
            if (String(profile?.id ?? "") !== String(profileId)) return;
            setImportProfileName(profile?.profile_name || "");
            // null (not an empty Set) means "no signal yet" — e.g. metadata
            // parsing hasn't finished — so the canvas knows to skip the
            // empty-column default rather than risk marking real data columns
            // as "Do not import".
            setColumnsWithData(
                profile?.profile_headers
                    ? new Set(profile.profile_headers.split(",").filter(Boolean))
                    : null,
            );
        });

        return () => {
            isActive = false;
        };
    }, [dispatchGetImportProfileById, profileId, projectId]);

    useEffect(() => {
        if (!profileId) {
            setColumnMappings([]);
            return undefined;
        }
        let cancelled = false;
        dispatchGetImportProfileMappingByProfile(profileId).then((existing) => {
            if (cancelled) return;
            setColumnMappings(parseColumnMappings(existing?.column_mappings));
        });
        return () => {
            cancelled = true;
        };
    }, [dispatchGetImportProfileMappingByProfile, profileId, savedMapping?.mappingId]);
    const {
        menuOrganizationProject,
        organizationProjects,
        project,
        fiscalYear: finalFiscalYear,
        importId: finalImportId,
        queryStatus,
    } = organizationAndProjectSelection({
        fetchResults,
        fetchStatus,
        component: ColumnMappingFlow,
        matchParams,
        props,
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId,
    });

    const dispatchSuggestCustomTemplateEntity = useCallback(
        (profileId, configSet, availableEntities) =>
            dispatch(
                suggestCustomTemplateEntityAction(
                    profileId,
                    configSet,
                    availableEntities,
                    organizationProjects?.id?.toString() || "",
                ),
            ),
        [dispatch, organizationProjects],
    );
    const dispatchSuggestCustomTemplateMapping = useCallback(
        (profileId, configSet, entityName, fileType) =>
            dispatch(
                suggestCustomTemplateMappingAction(
                    profileId,
                    configSet,
                    entityName,
                    organizationProjects?.id?.toString() || "",
                    undefined,
                    fileType,
                ),
            ),
        [dispatch, organizationProjects],
    );
    const dispatchSuggestCustomTemplateFiscalYear = useCallback(
        (currentProfileId, years) =>
            dispatch(
                suggestCustomTemplateFiscalYearAction(
                    currentProfileId,
                    years,
                    organizationProjects?.id?.toString() || "",
                ),
            ),
        [dispatch, organizationProjects],
    );
    const dispatchGetImportProfileIssues = useCallback(
        (profileId, mappingId, configSet, entityName, projectId) =>
            dispatch(
                getImportProfileIssuesAction(
                    profileId,
                    mappingId,
                    configSet,
                    entityName,
                    projectId,
                ),
            ),
        [dispatch],
    );
    const dispatchFixImportProfileCell = useCallback(
        (profileId, projectId, clbId, column, value) =>
            dispatch(fixImportProfileCellAction(profileId, projectId, clbId, column, value)),
        [dispatch],
    );
    const dispatchSuggestDataFixes = useCallback(
        (currentProfileId, currentProjectId, columnNames) =>
            dispatch(
                suggestDataFixesAction(
                    currentProfileId,
                    currentProjectId,
                    organizationProjects?.id?.toString() || "",
                    columnNames,
                ),
            ),
        [dispatch, organizationProjects],
    );
    const dispatchAcceptDataFixes = useCallback(
        (currentProfileId, currentProjectId, fixes) =>
            dispatch(acceptDataFixesAction(currentProfileId, currentProjectId, fixes)),
        [dispatch],
    );
    const { loading = true, success = false } = queryStatus || {};
    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);

    useEffect(() => {
        applyThemeWithPriority(organizationProjects, projectId, updateThemes, "ColumnMappingFlow");
    }, [organizationProjects, projectId, updateThemes]);

    fiscalYear = finalFiscalYear ?? fiscalYear;
    importId = finalImportId ?? importId;

    const fiscalYearOptions = useMemo(
        () => buildFiscalYearOptions(project?.fiscalYears, fiscalYear),
        [project?.fiscalYears, fiscalYear],
    );
    const suggestedFiscalYear =
        aiFiscalYear || getSuggestedFiscalYear(fiscalYearOptions, fiscalYear, false);

    const { navData, params, routeQueries } = buildGenericMenu(
        modules,
        fetchResults,
        locale,
        fiscalYear,
        projectId,
        importId,
        project,
        location,
        matchParams,
        route,
    );

    const { items, headerItems, metadata, filters, rawDataEdit } = mapProperties(
        {
            items: "query(0).items",
            headerItems: "query(0).headerItems",
            metadata: "query(0).metadata",
            filters: "query(0).filters",
            rawDataEdit: "query(0).rawDataEdit",
        },
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );

    const headerComponents = buildHeaderComponents(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );

    const downloadableQueries = getDownloadableQueries(matchRoutes, route, location, params);

    const { navigation } = generateNavigation({
        modules,
        asideRepositoryNavigationByModule,
        route,
        fetchResults,
        fetchStatus,
        component: ColumnMappingFlow,
        params,
        fiscalYear,
        importId,
        props,
        navData,
        i18n,
        locale,
    });

    const { navProps, staticLinks } = buildNavProps(
        buildStaticLinks,
        navigation,
        history,
        matchParams,
        fiscalYear,
        importId,
        i18n,
        menuOrganizationProject,
        projectId,
        cleanPropertiesNestedInMenu,
        locale,
        proxy,
        params,
        userInformation,
        modules,
        undefined,
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: { ...params, fiscalYear, importId },
        staticLinks,
        repositoryStatus: { status: 1 },
    };

    if (loading) {
        return LoadingComponent;
    }

    const {
        step: s,
        imported: imp,
        allMapped,
        savingMapping,
        loadingAiMapping,
        selectedEntityName,
        entityCanProceed,
        onCancel,
        onChangeEntity,
        onEntityNext,
        onNext,
        onSuggestMapping: onSuggestMappingAction,
    } = canvasActions;

    const canMutateTranslations = checkRolesByOrganizationProject(userInformation, projectId, [
        UserRoles.ADMIN,
        UserRoles.OWNER,
        UserRoles.PP_SUPPORT,
        UserRoles.SUPPORT,
    ]);
    const nonOwnerTooltip = i18n`Only the project owner can manage translations.`;

    const handleProceedData = () => {
        if (!savedMapping?.mappingId) return;
        setProceedError(null);
        setAiFiscalYear("");
        setAiFiscalYearScore(null);
        setAiFiscalYearRationale("");
        setShowProceedDialog(true);
        if (!hasAiSuggestions || !profileId) {
            setAiFiscalYearLoading(false);
            return;
        }
        const seq = ++fiscalYearSuggestSeq.current;
        setAiFiscalYearLoading(true);
        dispatchSuggestCustomTemplateFiscalYear(profileId, fiscalYearOptions)
            .then((result) => {
                if (seq !== fiscalYearSuggestSeq.current) return;
                if (!result?.fiscal_year) return;
                setAiFiscalYear(String(result.fiscal_year));
                setAiFiscalYearScore(typeof result.score === "number" ? result.score : null);
                setAiFiscalYearRationale(
                    typeof result.rationale === "string" ? result.rationale : "",
                );
            })
            .finally(() => {
                if (seq !== fiscalYearSuggestSeq.current) return;
                setAiFiscalYearLoading(false);
            });
    };

    const handleProceedCancel = () => {
        if (proceeding) return;
        fiscalYearSuggestSeq.current += 1;
        setAiFiscalYearLoading(false);
        setShowProceedDialog(false);
        setAiFiscalYear("");
        setAiFiscalYearScore(null);
        setAiFiscalYearRationale("");
    };

    const handleProceedConfirm = async (selectedFiscalYear) => {
        if (proceeding || !savedMapping?.mappingId || !profileId || !projectId || !selectedFiscalYear) return;
        const dataFileImportId = resolveProceedImportId(project?.fiscalYears?.[selectedFiscalYear]);
        trackUserAction("mapping_run_started", {
            profile_id: profileId,
            mapping_id: savedMapping.mappingId,
            fiscal_year: String(selectedFiscalYear),
            import_id: dataFileImportId,
        });
        setProceeding(true);
        setProceedError(null);
        try {
            const result = await dispatchRunCustomTemplateMapping({
                profileId,
                mappingId: savedMapping.mappingId,
                projectId: String(projectId),
                fiscalYear: String(selectedFiscalYear),
                importId: dataFileImportId,
            });
            if (result?.success) {
                setShowProceedDialog(false);
                setImportComplete(true);
            } else {
                setProceedError(result?.message || i18n`Failed to run mapping. Please try again.`);
            }
        } catch (_error) {
            setProceedError(i18n`Failed to run mapping. Please try again.`);
        } finally {
            setProceeding(false);
        }
    };

    const handleBackToList = () => {
        history.push(`/${locale}/${projectId}/${fiscalYear}/full/import_mapping/import_profiles`);
    };

    const routeImportId = importId || "full";
    const importMappingBasePath = buildImportMappingBasePath({
        locale,
        projectId,
        fiscalYear,
        importId: routeImportId,
    });
    const logEntity = resolveImportProfileLogEntity({
        profileName: importProfileName,
        profileId,
    });
    const processingLogEntityName = savedMapping?.entityName || null;

    const showMenuMessage = (message) => {
        setMenuSnackbar({ message, type: "error" });
    };

    const onProceed = () => {
        if (activeTab === "staging") {
            handleProceedData();
            return;
        }
        if (savedMapping?.mappingId || mappingPersisted) {
            handleParentTabChange(PARENT_TAB_STAGING);
            return;
        }
        if (allMapped && onNext) {
            onNext();
            return;
        }
        showMenuMessage(i18n`Complete column mapping before proceeding.`);
    };

    const onEditTemplate = () => {
        if (!profileId) return;
        history.push(
            buildCustomTemplateEditPath({
                locale,
                projectId,
                fiscalYear,
                importId: routeImportId,
                profileId,
            }),
        );
    };

    const onDataProcessingLog = () => {
        history.push(
            buildDataProcessingLogPath(
                importMappingBasePath,
                buildEntityNameLogFilter(processingLogEntityName),
            ),
        );
    };

    const onDataChangeLog = () => {
        history.push(
            buildDataChangeLogPath(importMappingBasePath, buildEntityLogFilter(logEntity)),
        );
    };

    const onDeleteTemplate = () => {
        if (!profileId) return;
        pushDialog("ConfirmDialog", i18n`Delete template`, {
            title: i18n`Are you sure you want to delete this template?`,
            text: importProfileName || "",
            promptText: i18n`Please confirm that you want to proceed!`,
        })
            .then(() =>
                dispatchDeleteImportProfile(profileId, projectId).then((success) => {
                    if (success) {
                        handleBackToList();
                        return;
                    }
                    showMenuMessage(i18n`Failed to delete template.`);
                }),
            )
            .catch(() => null);
    };

    const showStagingDataTab = !!profileId;

    const showMappingActions = activeTab === "mapping";
    const headerActions = showMappingActions
        ? [
              imp
                  ? null
                  : s === FLOW_STEP_INDEX.ChooseEntity ? (
                        <SovosButton
                            key="cancel-entity"
                            variant="text"
                            onClick={() => history.goBack()}
                        >
                            {i18n`Cancel`}
                        </SovosButton>
                    ) : null,
              imp
                  ? null
                  : s === FLOW_STEP_INDEX.ChooseEntity ? (
                        <SovosButton
                            key="next-entity"
                            variant="contained"
                            disabled={!entityCanProceed}
                            onClick={onEntityNext}
                        >
                            {i18n`Next`}
                        </SovosButton>
                    ) : null,
              imp ? null : s === FLOW_STEP_INDEX.DefineMapping && onChangeEntity ? (
                  <SovosButton key="change-entity" variant="text" onClick={onChangeEntity}>
                      {i18n`Change entity`}
                  </SovosButton>
              ) : null,
              imp ? null : s === FLOW_STEP_INDEX.DefineMapping && onCancel ? (
                  <SovosButton key="cancel" variant="text" onClick={onCancel}>
                      {i18n`Cancel`}
                  </SovosButton>
              ) : null,
              imp ? null : s === FLOW_STEP_INDEX.DefineMapping && onSuggestMappingAction && selectedEntityName ? (
                  <SoviGradientOutlineButton
                      key="ai"
                      onClick={onSuggestMappingAction}
                      disabled={loadingAiMapping}
                      startIcon={
                          loadingAiMapping ? (
                              <CircularProgress size={14} sx={{ color: "primary.main" }} />
                          ) : (
                              <SoviAiIcon />
                          )
                      }
                  >
                      {i18n`Suggest mappings (AI)`}
                  </SoviGradientOutlineButton>
              ) : null,
              imp ? null : s === FLOW_STEP_INDEX.DefineMapping ? (
                  <SovosButton
                      key="next"
                      variant="contained"
                      disabled={!allMapped || savingMapping}
                      onClick={onNext}
                      startIcon={<CheckCircle fontSize="small" />}
                  >
                      {savingMapping ? i18n`Confirming...` : i18n`Confirm mapping`}
                  </SovosButton>
              ) : null,
          ].filter(Boolean)
        : [
                <SovosTooltip
                    key="fix-by-ai-tooltip"
                    title={
                        hasAiSuggestions
                            ? i18n`Analyse errors and suggest corrections with Sovi AI`
                            : i18n`Sovi AI suggestions are not enabled for this project.`
                    }
                    placement="top"
                >
                    <span>
                        <SovosBox
                            sx={{
                                background: hasAiSuggestions
                                    ? SOVI_BUTTON_GRADIENT
                                    : "action.disabledBackground",
                                borderRadius: 1,
                                display: "inline-flex",
                                p: "1px",
                            }}
                        >
                            <SovosButton
                                variant="text"
                                size="small"
                                startIcon={
                                    stagingAiBusy ? (
                                        <CircularProgress size={14} sx={{ color: "inherit" }} />
                                    ) : (
                                        <SovosBox aria-hidden sx={SOVI_PLACEHOLDER_ORB_SX} />
                                    )
                                }
                                onClick={() => stagingDataPanelRef.current?.runAiFixes()}
                                disabled={!hasAiSuggestions || stagingAiBusy}
                                sx={{
                                    bgcolor: "background.paper",
                                    "&:hover": { bgcolor: "action.hover" },
                                }}
                            >
                                {stagingAiBusy ? i18n`Analysing…` : i18n`Fix by AI`}
                            </SovosButton>
                        </SovosBox>
                    </span>
                </SovosTooltip>,
                <SovosTooltip
                    key="proceed-data-tooltip"
                    title={canMutateTranslations ? "" : nonOwnerTooltip}
                    placement="top"
                >
                    <span>
                        <SovosButton
                            variant="contained"
                            size="small"
                            onClick={handleProceedData}
                            disabled={
                                !canMutateTranslations || proceeding || !savedMapping?.mappingId
                            }
                        >
                            {i18n`Proceed data`}
                        </SovosButton>
                    </span>
                </SovosTooltip>,
            ];

    return (
        <GlobalPageLayout
            globalLayoutProps={globalLayoutProps}
            modules={modules}
            headerProps={{
                fiscalYears: undefined,
                importIds: undefined,
                downloadableQueries,
                title: "",
                headerComponents,
                customHeader: (
                    <Header
                        step={canvasStep}
                        frameworkName={importProfileName}
                        imported={canvasImported}
                        mappingPersisted={mappingPersisted}
                        mappingLoading={mappingLoading}
                        actions={headerActions}
                        onStepClick={(stepNum) => canvasActions.onStepClick?.(stepNum)}
                        activeTab={activeTab}
                        onTabChange={handleParentTabChange}
                        showStagingDataTab={showStagingDataTab}
                        onProceed={onProceed}
                        onEditTemplate={onEditTemplate}
                        onDataProcessingLog={onDataProcessingLog}
                        onDataChangeLog={onDataChangeLog}
                        onDeleteTemplate={onDeleteTemplate}
                        i18n={i18n}
                    />
                ),
            }}
            headerComponentsMapper={() => (headerComponentProperties) => (
                <headerComponentProperties.Component
                    key="column-mapping"
                    pushModal={pushModal}
                    i18n={i18n}
                    reload={reload}
                    projectId={projectId}
                    user={userInformation}
                    {...headerComponentProperties.props}
                />
            )}
            fetch={props.fetch}
            route={route}
            matchParams={matchParams}
            menu={props.menu}
            isLoadingContent={() => false}
        >
            <SovosBox sx={{ display: activeTab === "mapping" ? "block" : "none" }}>
                <ColumnMappingCanvas
                    isActive={activeTab === "mapping"}
                    items={items}
                    headerItems={headerItems}
                    metadata={metadata}
                    filters={filters}
                    rawDataEdit={rawDataEdit}
                    columnsWithData={columnsWithData}
                    profileId={profileId}
                    projectId={projectId}
                    fiscalYear={fiscalYear}
                    importId={parseInt(importId, 10) || undefined}
                    onFetchMappingContexts={dispatchGetAvailableMappingContexts}
                    onFetchEntities={dispatchGetAvailableEntities}
                    onFetchCdmFields={dispatchGetAvailableCdmFields}
                    onFetchExistingMapping={dispatchGetImportProfileMappingByProfile}
                    onCreateMapping={dispatchCreateImportProfileMapping}
                    onUpdateMapping={dispatchUpdateImportProfileMapping}
                    onSuggestEntity={
                        hasAiSuggestions ? dispatchSuggestCustomTemplateEntity : undefined
                    }
                    onSuggestMapping={
                        hasAiSuggestions ? dispatchSuggestCustomTemplateMapping : undefined
                    }
                    projectModules={modules}
                    onStepChange={handleStepChange}
                    onActionStateChange={handleActionStateChange}
                    onMappingSaved={handleMappingSaved}
                    onMappingConfirmed={handleMappingConfirmed}
                />
            </SovosBox>
            {activeTab === "staging" && showStagingDataTab && (
                <StagingDataPanel
                    key={`${profileId}:${savedMapping?.mappingId || "unsaved"}`}
                    ref={stagingDataPanelRef}
                    profileId={profileId}
                    projectId={projectId}
                    mappingId={savedMapping?.mappingId}
                    configSet={savedMapping?.configSet}
                    entityName={savedMapping?.entityName}
                    items={items}
                    headerItems={headerItems}
                    columnMappings={columnMappings}
                    metadata={metadata}
                    canMutateTranslations={canMutateTranslations}
                    nonOwnerTooltip={nonOwnerTooltip}
                    aiSuggestionsEnabled={hasAiSuggestions}
                    onAiBusyChange={setStagingAiBusy}
                    onFetchIssues={dispatchGetImportProfileIssues}
                    onFixCell={dispatchFixImportProfileCell}
                    onSuggestFixes={hasAiSuggestions ? dispatchSuggestDataFixes : undefined}
                    onBulkAcceptFixes={hasAiSuggestions ? dispatchAcceptDataFixes : undefined}
                    onListStagingTranslations={dispatchListStagingTranslations}
                    onCreateStagingTranslation={dispatchCreateStagingTranslation}
                    onUpdateStagingTranslation={dispatchUpdateStagingTranslation}
                    onToggleStagingTranslationState={dispatchToggleStagingTranslationState}
                    onSoftDeleteStagingTranslation={dispatchSoftDeleteStagingTranslation}
                    onApplyStagingTranslation={dispatchApplyStagingTranslation}
                    onStagingDataChanged={reload}
                />
            )}
            <ImportCompleteView
                open={importComplete}
                i18n={i18n}
                onClose={() => setImportComplete(false)}
            />
            <ProceedFiscalYearDialog
                open={showProceedDialog}
                fiscalYears={fiscalYearOptions}
                suggestedFiscalYear={suggestedFiscalYear}
                suggestedScore={aiFiscalYearScore}
                suggestedRationale={aiFiscalYearRationale}
                suggesting={aiFiscalYearLoading}
                confirming={proceeding}
                i18n={i18n}
                onCancel={handleProceedCancel}
                onConfirm={handleProceedConfirm}
            />
            <SovosSnackbar
                open={Boolean(proceedError)}
                message={proceedError || ""}
                onClose={() => setProceedError(null)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
            <SovosSnackbar
                open={Boolean(menuSnackbar?.message)}
                message={menuSnackbar?.message || ""}
                onClose={() => setMenuSnackbar(null)}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </GlobalPageLayout>
    );
};

ColumnMappingFlow.queries = (args, userModulesByProject) => {
    const queries = filteredQueriesByModules(userModulesByProject);
    const deepCopyQueries = JSON.parse(JSON.stringify(queries));
    if (deepCopyQueries) {
        //needed to change arguments to the respective value
        deepCopyQueries.forEach((query) =>
            Object.keys(query.args).forEach((k) =>
                query.args[k] ? query.args[k] : (query.args[k] = args[k] || query.args[k]),
            ),
        );
    }

    const allQueries = [
        {
            resource: "menu_organizations_projects",
            body: "id,name,typeEntity,nestedAccounts{id,name,typeEntity}",
            forceRefresh: true,
        },
        {
            resource: "organization_projects_by_projectId",
            args: {
                projectId: args.projectId,
            },
            body: "id,name,maxFiscalYear,fiscalYears,calendarYears,mosaicThemeConfig,projects{id,name,fiscalYears,status,taxRegistrationNumber,active,projectExpired,metadata,mosaicThemeConfig,modules{navigation_by_import_id,name,default_route,default_module}},organizationExpired, organizationActive, maxFiscalYear",
            forceRefresh: true,
        },
        {
            resource: "custom_csv_raw_data",
            args: {
                entity: args.entityRawData || "",
                projectId: args.projectId,
                fiscalYear: args.fiscalYear,
                importId: args.importId,
            },
            body: "items,headerItems,metadata{total},rawDataEdit",
            isDatagridReq: true,
        },
    ];
    return [...allQueries, ...deepCopyQueries];
};

export default connect(({ user, fetch, menu }) => ({ user, fetch, menu }), {
    logout,
    pushModal,
    popModal,
    removeModal,
    toggleMenu,
    setLocale,
    pushDialog,
})(withRouter(ColumnMappingFlow));
